/**
 * Helpers for syncing document-search folder navigation with the URL.
 *
 * Folder depth lives under a reserved browse prefix so it never clashes with
 * the document viewer route `/document/search/:id`:
 *
 *   /document/search
 *   /document/search/browse/B/B001                 ← unique codes
 *   /document/search/browse/I.LMLG.SN-19.LG        ← id when codes collide
 *   /document/search/{documentId}                  ← viewer (unchanged)
 *
 * Display names stay in the UI only. URL segments prefer a compact unique
 * `code` among siblings and fall back to unique `id` when codes collide
 * (installed landing gears, engines, APUs).
 */

export const DOCUMENT_SEARCH_BASE_PATH = "/document/search";
export const DOCUMENT_SEARCH_BROWSE_BASE = "/document/search/browse";
/** @deprecated Legacy query param; still decoded for old bookmarks then redirected. */
export const DOCUMENT_SEARCH_PATH_PARAM = "path";
export const DOCUMENT_SEARCH_GROUP_PARAM = "group";

/** Encode path segments once; join with literal `/` for browse URLs. */
export function encodeFolderPath(path: string[]): string {
  return path.map((segment) => encodeURIComponent(segment)).join("/");
}

/**
 * Decode folder segments from a browse splat or a legacy `?path=` value.
 * Tolerates leftover `%20` encoding after the router/browser has decoded once.
 */
export function decodeFolderPath(raw: string | null | undefined): string[] {
  if (!raw || !raw.trim()) {
    return [];
  }

  return raw
    .split("/")
    .filter((segment) => segment.length > 0)
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    });
}

export function folderPathsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((segment, index) => segment === b[index]);
}

export interface FolderPathNode {
  id: string;
  name: string;
  code: string;
  children: FolderPathNode[];
}

export interface FolderUrlIdentity {
  id: string;
  name: string;
  code: string;
}

function trimIdentity(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function isCustomCode(code: string): boolean {
  return code.toUpperCase() === "CUSTOM";
}

/** Compact token that does not need extra encoding beyond `/` delimiters. */
function isCompactToken(value: string): boolean {
  return value.length > 0 && !/\s/.test(value) && !value.includes("/");
}

function idLeafSegment(id: string): string {
  const parts = trimIdentity(id).split("/").filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : "";
}

function countMatchingSiblings(
  siblings: readonly FolderUrlIdentity[],
  matches: (folder: FolderUrlIdentity) => boolean
): number {
  let count = 0;
  for (const sibling of siblings) {
    if (matches(sibling)) {
      count += 1;
    }
  }
  return count;
}

function isUniqueAmongSiblings(
  siblings: readonly FolderUrlIdentity[],
  matches: (folder: FolderUrlIdentity) => boolean
): boolean {
  return countMatchingSiblings(siblings, matches) === 1;
}

/**
 * Compact URL key for a folder among its siblings.
 * Prefers unique IATA `code`; uses `id` when codes collide or for CUSTOM.
 * Falls back to `name` only when nothing else exists.
 */
export function getFolderUrlKey(
  folder: FolderUrlIdentity,
  siblings: readonly FolderUrlIdentity[] = [folder]
): string {
  const peers = siblings.length > 0 ? siblings : [folder];
  const code = trimIdentity(folder.code);
  const id = trimIdentity(folder.id);

  if (isCustomCode(code)) {
    return id || folder.name;
  }

  if (
    isCompactToken(code) &&
    isUniqueAmongSiblings(peers, (sibling) => trimIdentity(sibling.code) === code)
  ) {
    return code;
  }

  if (id) {
    if (id.includes("/")) {
      const leaf = idLeafSegment(id);
      if (
        isCompactToken(leaf) &&
        !isCustomCode(leaf) &&
        isUniqueAmongSiblings(
          peers,
          (sibling) => idLeafSegment(sibling.id) === leaf
        )
      ) {
        return leaf;
      }
    }

    return id;
  }

  return folder.name;
}

type FolderUrlKeyMatch =
  | { status: "found"; folder: FolderPathNode }
  | { status: "ambiguous" }
  | { status: "none" };

/**
 * Resolves a URL segment against siblings. Exact `id` wins; duplicate codes
 * never first-match (that is what opened the wrong landing gear).
 */
export function matchFolderByUrlKey(
  siblings: readonly FolderPathNode[],
  key: string
): FolderUrlKeyMatch {
  if (!key || siblings.length === 0) {
    return { status: "none" };
  }

  const exactIds = siblings.filter((folder) => folder.id === key);
  if (exactIds.length === 1) {
    return { status: "found", folder: exactIds[0] };
  }
  if (exactIds.length > 1) {
    return { status: "ambiguous" };
  }

  const leafMatches = siblings.filter((folder) =>
    folder.id.endsWith(`/${key}`)
  );
  if (leafMatches.length === 1) {
    return { status: "found", folder: leafMatches[0] };
  }
  if (leafMatches.length > 1) {
    return { status: "ambiguous" };
  }

  const codeMatches = siblings.filter(
    (folder) => trimIdentity(folder.code) === key && !isCustomCode(folder.code)
  );
  if (codeMatches.length === 1) {
    return { status: "found", folder: codeMatches[0] };
  }
  if (codeMatches.length > 1) {
    return { status: "ambiguous" };
  }

  const nameMatches = siblings.filter((folder) => folder.name === key);
  if (nameMatches.length === 1) {
    return { status: "found", folder: nameMatches[0] };
  }
  if (nameMatches.length > 1) {
    return { status: "ambiguous" };
  }

  return { status: "none" };
}

export interface ResolveBrowseUrlKeysResult {
  /** Display names for breadcrumbs / navigationState.currentPath */
  namePath: string[];
  /** Canonical URL keys (code/id) for the matched prefix */
  urlKeys: string[];
  folder: FolderPathNode | null;
  /**
   * True when a URL segment matched more than one sibling and was ignored.
   * Callers must not canonicalize that URL onto the first match.
   */
  ambiguous: boolean;
}

/**
 * Walks the folder tree using URL keys (code / id / legacy name).
 * Returns the longest valid prefix plus display names for the UI.
 */
export function resolveBrowseUrlKeys(
  urlKeys: string[],
  folders: FolderPathNode[]
): ResolveBrowseUrlKeysResult {
  if (urlKeys.length === 0) {
    return { namePath: [], urlKeys: [], folder: null, ambiguous: false };
  }

  if (folders.length === 0) {
    return { namePath: [], urlKeys, folder: null, ambiguous: false };
  }

  const namePath: string[] = [];
  const validKeys: string[] = [];
  let currentFolders = folders;
  let currentFolder: FolderPathNode | null = null;

  for (const key of urlKeys) {
    const match = matchFolderByUrlKey(currentFolders, key);
    if (match.status === "ambiguous") {
      return {
        namePath,
        urlKeys: validKeys,
        folder: currentFolder,
        ambiguous: true,
      };
    }

    if (match.status === "none") {
      break;
    }

    currentFolder = match.folder;
    namePath.push(match.folder.name);
    validKeys.push(getFolderUrlKey(match.folder, currentFolders));
    currentFolders = match.folder.children ?? [];
  }

  return {
    namePath,
    urlKeys: validKeys,
    folder: currentFolder,
    ambiguous: false,
  };
}

/**
 * Converts a display-name path into canonical browse URL keys.
 */
export function toBrowseUrlKeys(
  namePath: string[],
  folders: FolderPathNode[]
): string[] {
  if (namePath.length === 0) {
    return [];
  }

  const keys: string[] = [];
  let currentFolders = folders;

  for (const name of namePath) {
    const matched = currentFolders.find((folder) => folder.name === name);
    if (!matched) {
      break;
    }

    keys.push(getFolderUrlKey(matched, currentFolders));
    currentFolders = matched.children ?? [];
  }

  return keys;
}

/**
 * @deprecated Prefer resolveBrowseUrlKeys. Treats entries as names or url keys.
 */
export function resolveValidFolderPath(
  path: string[],
  folders: FolderPathNode[]
): string[] {
  if (path.length === 0) {
    return [];
  }

  if (folders.length === 0) {
    return path;
  }

  return resolveBrowseUrlKeys(path, folders).namePath;
}

/** `/document/search` or `/document/search/browse/Seg1/Seg2`. */
export function buildDocumentSearchBrowsePathname(path: string[]): string {
  if (path.length === 0) {
    return DOCUMENT_SEARCH_BASE_PATH;
  }

  return `${DOCUMENT_SEARCH_BROWSE_BASE}/${encodeFolderPath(path)}`;
}

/**
 * Builds the query string for search navigation.
 * Drops legacy `path` and sets/clears `group` for grouped mode.
 */
export function buildDocumentSearchSearchString(
  currentSearch: string,
  options?: { group?: string | null }
): string {
  const params = new URLSearchParams(
    currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch
  );

  params.delete(DOCUMENT_SEARCH_PATH_PARAM);

  const group = options?.group ?? null;
  if (group) {
    params.set(DOCUMENT_SEARCH_GROUP_PARAM, group);
  } else {
    params.delete(DOCUMENT_SEARCH_GROUP_PARAM);
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export function isDocumentSearchBrowsePath(pathname: string): boolean {
  return (
    pathname === DOCUMENT_SEARCH_BROWSE_BASE ||
    pathname.startsWith(`${DOCUMENT_SEARCH_BROWSE_BASE}/`)
  );
}
